/**
 * Subscription Diagnostics Panel
 * 
 * Debug component to run subscription diagnostics and display results
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, Play, Copy } from 'lucide-react';
import { subscriptionDiagnostics, type DiagnosticResult } from '@/utils/diagnostics/SubscriptionDiagnostics';
import { usePermissions } from '@/contexts/unified-permissions';

export const SubscriptionDiagnosticsPanel: React.FC = () => {
  const { user } = usePermissions();
  const [isRunning, setIsRunning] = useState(false);
  const [results, setResults] = useState<DiagnosticResult[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const runDiagnostics = async () => {
    setIsRunning(true);
    try {
      const diagnosticResults = await subscriptionDiagnostics.runFullDiagnostics(user?.id);
      setResults(diagnosticResults);
      
      // Auto-expand categories with errors or warnings
      const categoriesWithIssues = new Set(
        diagnosticResults
          .filter(r => r.status === 'error' || r.status === 'warning')
          .map(r => r.category)
      );
      setExpandedCategories(categoriesWithIssues);
    } catch (error) {
      console.error('Failed to run diagnostics:', error);
    } finally {
      setIsRunning(false);
    }
  };

  const toggleCategory = (category: string) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const copyResults = () => {
    const summary = subscriptionDiagnostics.generateSummary();
    const detailedResults = results.map(r => 
      `[${r.timestamp}] ${r.category} ${r.status.toUpperCase()}: ${r.message}${r.data ? '\nData: ' + JSON.stringify(r.data, null, 2) : ''}`
    ).join('\n\n');
    
    const fullReport = `${summary}\n\n--- Detailed Results ---\n${detailedResults}`;
    navigator.clipboard.writeText(fullReport);
  };

  const getStatusBadge = (status: DiagnosticResult['status']) => {
    const variants = {
      success: 'default',
      warning: 'secondary',
      error: 'destructive',
      info: 'outline'
    } as const;

    const icons = {
      success: '✅',
      warning: '⚠️',
      error: '❌',
      info: 'ℹ️'
    };

    return (
      <Badge variant={variants[status]}>
        {icons[status]} {status.toUpperCase()}
      </Badge>
    );
  };

  const groupedResults = results.reduce((acc, result) => {
    if (!acc[result.category]) {
      acc[result.category] = [];
    }
    acc[result.category].push(result);
    return acc;
  }, {} as Record<string, DiagnosticResult[]>);

  const getCategorySummary = (categoryResults: DiagnosticResult[]) => {
    const errors = categoryResults.filter(r => r.status === 'error').length;
    const warnings = categoryResults.filter(r => r.status === 'warning').length;
    const successes = categoryResults.filter(r => r.status === 'success').length;
    
    return { errors, warnings, successes };
  };

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span>🔍 Subscription Diagnostics</span>
          <div className="flex gap-2">
            {results.length > 0 && (
              <Button variant="outline" size="sm" onClick={copyResults}>
                <Copy className="w-4 h-4 mr-2" />
                Copy Results
              </Button>
            )}
            <Button 
              onClick={runDiagnostics} 
              disabled={isRunning}
              size="sm"
            >
              <Play className="w-4 h-4 mr-2" />
              {isRunning ? 'Running...' : 'Run Diagnostics'}
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      
      <CardContent>
        {!results.length && !isRunning && (
          <div className="text-center py-8 text-muted-foreground">
            Click "Run Diagnostics" to analyze subscription detection
          </div>
        )}

        {isRunning && (
          <div className="text-center py-8">
            <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full mx-auto mb-2"></div>
            <p className="text-muted-foreground">Running diagnostics...</p>
          </div>
        )}

        {results.length > 0 && (
          <div className="space-y-4">
            {/* Summary */}
            <div className="bg-muted p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Summary</h3>
              <pre className="text-sm whitespace-pre-wrap">
                {subscriptionDiagnostics.generateSummary()}
              </pre>
            </div>

            {/* Detailed Results by Category */}
            <div className="space-y-2">
              {Object.entries(groupedResults).map(([category, categoryResults]) => {
                const isExpanded = expandedCategories.has(category);
                const summary = getCategorySummary(categoryResults);
                
                return (
                  <Collapsible key={category} open={isExpanded}>
                    <CollapsibleTrigger 
                      className="w-full"
                      onClick={() => toggleCategory(category)}
                    >
                      <div className="flex items-center justify-between w-full p-3 bg-muted/50 rounded-lg hover:bg-muted">
                        <div className="flex items-center gap-2">
                          {isExpanded ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                          <span className="font-medium">{category}</span>
                        </div>
                        <div className="flex gap-1">
                          {summary.successes > 0 && (
                            <Badge variant="default" className="text-xs">
                              {summary.successes} ✅
                            </Badge>
                          )}
                          {summary.warnings > 0 && (
                            <Badge variant="secondary" className="text-xs">
                              {summary.warnings} ⚠️
                            </Badge>
                          )}
                          {summary.errors > 0 && (
                            <Badge variant="destructive" className="text-xs">
                              {summary.errors} ❌
                            </Badge>
                          )}
                        </div>
                      </div>
                    </CollapsibleTrigger>
                    
                    <CollapsibleContent>
                      <div className="mt-2 space-y-2">
                        {categoryResults.map((result, index) => (
                          <div 
                            key={index}
                            className="p-3 border rounded-lg bg-background"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <span className="text-sm font-medium">{result.message}</span>
                              {getStatusBadge(result.status)}
                            </div>
                            
                            <div className="text-xs text-muted-foreground mb-2">
                              {new Date(result.timestamp).toLocaleString()}
                            </div>
                            
                            {result.data && (
                              <details className="mt-2">
                                <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">
                                  View Data
                                </summary>
                                <pre className="mt-2 p-2 bg-muted rounded text-xs overflow-auto max-h-40">
                                  {JSON.stringify(result.data, null, 2)}
                                </pre>
                              </details>
                            )}
                          </div>
                        ))}
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                );
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
